/**
 * Pagina admin per gestire la Campagna Feedback Eventi.
 * - Vedi destinatari
 * - Scarica Excel
 * - Invia anteprima a info@ita318.it
 * - Invia a tutti agenti+distributori
 */
import { useEffect, useState } from "react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  Mail, Send, Download, Users, FileSpreadsheet, AlertCircle,
  CheckCircle2, Eye, History, Loader2,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function FeedbackCampaignPage() {
  const [recipients, setRecipients] = useState([]);
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);

  const load = () => {
    fetch(`${API}/feedback-campaign/recipients`).then(r => r.json()).then(d => setRecipients(d.items || []));
    fetch(`${API}/feedback-campaign/log`).then(r => r.json()).then(d => setLog(d.items || []));
  };
  useEffect(load, []);

  const sendPreview = async () => {
    setBusy("preview");
    try {
      const r = await fetch(`${API}/feedback-campaign/preview`, { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
      const d = await r.json();
      if (r.ok) toast.success(`✉ Anteprima inviata a ${d.to} (allegato ${(d.attachment_bytes / 1024).toFixed(1)} KB)`);
      else toast.error(d.detail || "Errore invio anteprima");
      load();
    } catch (e) { toast.error("Errore di rete"); }
    setBusy("");
  };

  const sendAll = async () => {
    setBusy("all");
    setShowConfirm(false);
    try {
      const r = await fetch(`${API}/feedback-campaign/send-all`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ target: "agent_distributor" }),
      });
      const d = await r.json();
      if (r.ok) {
        toast.success(`✓ Inviate ${d.sent_count} mail · ${d.failed_count} fallite`);
      } else {
        toast.error(d.detail || "Errore invio massivo");
      }
      load();
    } catch (e) { toast.error("Errore di rete"); }
    setBusy("");
  };

  const downloadExcel = () => {
    window.open(`${API}/feedback-campaign/excel`, "_blank");
  };

  const agentCount = recipients.filter(r => r.type === "agent").length;
  const distribCount = recipients.filter(r => r.type === "distributor").length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#fdf6f3] via-white to-[#fef3e8] py-10 px-6">
      <div className="max-w-5xl mx-auto space-y-6">

        {/* HEADER */}
        <div className="bg-gradient-to-br from-[#5A3349] via-[#7a4a5e] to-[#B4607A] rounded-3xl p-8 text-white shadow-2xl">
          <div className="flex items-center gap-3 mb-3">
            <div className="bg-white/15 backdrop-blur-md p-3 rounded-2xl"><Mail className="w-7 h-7" /></div>
            <div>
              <p className="text-xs font-bold uppercase tracking-[3px] opacity-80">Campagna Email</p>
              <h1 className="text-3xl font-bold font-serif italic">Feedback Contatti Fiere</h1>
            </div>
          </div>
          <p className="text-rose-100 text-sm leading-relaxed max-w-2xl">
            Invio massivo della mail con Excel allegato a tutti gli agenti e distributori ODR per raccogliere
            il feedback sui contatti generati nelle fiere ed eventi recenti.
          </p>
        </div>

        {/* STATS DESTINATARI */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard icon={Users} label="Agenti" value={agentCount} color="violet" />
          <StatCard icon={Users} label="Distributori" value={distribCount} color="sky" />
          <StatCard icon={Mail} label="Email totali" value={recipients.length} color="rose" />
        </div>

        {/* AZIONI */}
        <div className="bg-white rounded-3xl shadow-sm border border-rose-100 p-6 space-y-4">
          <h3 className="font-bold text-[#5A3349] text-lg flex items-center gap-2">
            <Send className="w-5 h-5" /> Flusso di invio
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <ActionCard
              step="1"
              title="Scarica Excel"
              desc="Verifica i contatti che verranno allegati alla mail"
              Icon={FileSpreadsheet}
              onClick={downloadExcel}
              loading={false}
              color="emerald"
              data-testid="action-download-excel"
            />
            <ActionCard
              step="2"
              title="Anteprima a te"
              desc="Invia la mail SOLO a info@ita318.it per approvazione"
              Icon={Eye}
              onClick={sendPreview}
              loading={busy === "preview"}
              color="sky"
              data-testid="action-preview"
            />
            <ActionCard
              step="3"
              title="Invia a TUTTI"
              desc={`Manda a tutti i ${recipients.length} agenti e distributori`}
              Icon={Send}
              onClick={() => setShowConfirm(true)}
              loading={busy === "all"}
              color="rose"
              danger
              data-testid="action-send-all"
            />
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-900">
              <p className="font-semibold mb-1">Flusso consigliato</p>
              <p className="text-amber-800 leading-relaxed">
                Prima fai <strong>step 2 (Anteprima)</strong>. Controlla la mail su <code className="bg-amber-100 px-1.5 py-0.5 rounded text-xs">info@ita318.it</code>,
                apri l'Excel allegato. Se è tutto OK premi <strong>step 3</strong> per l'invio massivo.
              </p>
            </div>
          </div>
        </div>

        {/* DESTINATARI */}
        <div className="bg-white rounded-3xl shadow-sm border border-rose-100 p-6">
          <h3 className="font-bold text-[#5A3349] text-lg flex items-center gap-2 mb-4">
            <Users className="w-5 h-5" /> Lista destinatari ({recipients.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-96 overflow-auto">
            {recipients.map((r, i) => (
              <div key={i} className="flex items-center justify-between p-2.5 hover:bg-rose-50 rounded-lg text-sm border border-rose-50" data-testid={`recipient-${i}`}>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-[#5A3349] truncate">{r.name}</p>
                  <p className="text-xs text-gray-500 truncate">{r.email}</p>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full font-semibold shrink-0 ml-2 ${r.type === "agent" ? "bg-violet-100 text-violet-700" : "bg-sky-100 text-sky-700"}`}>
                  {r.type === "agent" ? "Agente" : "Distrib."}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* STORICO */}
        {log.length > 0 && (
          <div className="bg-white rounded-3xl shadow-sm border border-rose-100 p-6">
            <h3 className="font-bold text-[#5A3349] text-lg flex items-center gap-2 mb-4">
              <History className="w-5 h-5" /> Storico invii
            </h3>
            <div className="space-y-2">
              {log.map((l, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-xl border border-rose-50 bg-rose-50/30">
                  <div className="flex items-center gap-3">
                    {l.type === "preview" ? (
                      <span className="bg-sky-100 text-sky-700 text-xs px-2.5 py-1 rounded-full font-semibold">ANTEPRIMA</span>
                    ) : (
                      <span className="bg-emerald-100 text-emerald-700 text-xs px-2.5 py-1 rounded-full font-semibold">INVIO MASSIVO</span>
                    )}
                    <div>
                      <p className="text-sm font-medium text-[#5A3349]">
                        {l.type === "preview" ? `Anteprima a ${l.to}` : `${l.sent_count} inviate, ${l.failed_count} fallite`}
                      </p>
                      <p className="text-xs text-gray-500">{new Date(l.sent_at).toLocaleString("it-IT")}</p>
                    </div>
                  </div>
                  {l.type === "send_all" && l.failed_count > 0 && (
                    <span className="text-xs text-red-600">{l.failed_count} errori</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* MODALE CONFERMA INVIO MASSIVO */}
        {showConfirm && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowConfirm(false)}>
            <div className="bg-white rounded-3xl max-w-md w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="confirm-modal">
              <div className="flex items-center gap-3">
                <div className="bg-rose-100 p-2.5 rounded-2xl"><AlertCircle className="w-6 h-6 text-rose-600" /></div>
                <h3 className="text-xl font-bold text-[#5A3349]">Confermi l'invio?</h3>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed">
                Stai per inviare la mail con allegato Excel a <strong>{recipients.length} destinatari</strong>
                ({agentCount} agenti + {distribCount} distributori).
                Una volta inviate, le mail non possono essere annullate.
              </p>
              <p className="text-xs text-gray-500">L'invio richiederà circa <strong>{Math.ceil(recipients.length * 0.7)} secondi</strong>.</p>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowConfirm(false)} className="flex-1 h-12" data-testid="confirm-cancel">Annulla</Button>
                <Button onClick={sendAll} className="flex-1 h-12 bg-rose-600 hover:bg-rose-700" data-testid="confirm-send">
                  <Send className="w-4 h-4 mr-2" /> Sì, invia ora
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  const map = {
    rose: "from-rose-500 to-rose-600",
    violet: "from-violet-500 to-violet-600",
    sky: "from-sky-500 to-sky-600",
  };
  return (
    <div className={`bg-gradient-to-br ${map[color]} text-white rounded-2xl p-5 shadow-lg`}>
      <div className="flex items-center justify-between">
        <Icon className="w-8 h-8 opacity-80" />
        <span className="text-3xl font-bold">{value}</span>
      </div>
      <p className="text-sm mt-2 opacity-90">{label}</p>
    </div>
  );
}

function ActionCard({ step, title, desc, Icon, onClick, loading, color, danger }) {
  const map = {
    emerald: "border-emerald-200 bg-emerald-50 hover:border-emerald-400 text-emerald-700",
    sky: "border-sky-200 bg-sky-50 hover:border-sky-400 text-sky-700",
    rose: "border-rose-200 bg-rose-50 hover:border-rose-400 text-rose-700",
  };
  return (
    <button
      onClick={onClick}
      disabled={loading}
      className={`text-left p-5 rounded-2xl border-2 transition-all ${map[color]} ${loading ? "opacity-60" : "hover:shadow-md hover:-translate-y-0.5"} ${danger ? "ring-1 ring-rose-300" : ""}`}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-bold opacity-50">STEP {step}</span>
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Icon className="w-5 h-5" />}
      </div>
      <p className="font-bold text-[#5A3349]">{title}</p>
      <p className="text-xs text-gray-600 mt-1 leading-relaxed">{desc}</p>
    </button>
  );
}
